import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Plane, Clock, Calendar } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { getLeaderboard, getLeaderboardMonthLabel } from "@/lib/api";

const rankStyles = {
  1: "border-[var(--aiv-accent)]/60 bg-[var(--aiv-accent)]/5 text-[var(--aiv-accent)]",
  2: "border-slate-300/40 bg-slate-300/5 text-slate-200",
  3: "border-amber-700/50 bg-amber-700/10 text-amber-500",
};

const Rows = ({ entries, isLoading }) => {
  if (isLoading) {
    return (
      <p className="py-10 text-center text-sm text-[var(--aiv-text-2)]" data-testid="leaderboard-loading">
        Data Loading…
      </p>
    );
  }
  if (!entries || entries.length === 0) {
    return (
      <p className="py-10 text-center text-sm text-[var(--aiv-text-2)]" data-testid="leaderboard-empty">
        No flights logged yet this period.
      </p>
    );
  }
  return (
    <div className="space-y-3" data-testid="leaderboard-rows">
      <AnimatePresence>
        {entries.map((e, i) => (
          <motion.div
            key={e.callsign}
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.35, delay: i * 0.05 }}
            data-testid={`leaderboard-entry-${e.rank}`}
            className="flex items-center gap-4 rounded-xl border border-white/5 bg-[var(--aiv-surface)] px-4 py-3.5 transition-colors duration-200 hover:border-[#8A1538]/50"
          >
            <span
              className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border font-mono-data text-sm font-bold ${
                rankStyles[e.rank] || "border-white/10 text-[var(--aiv-text-2)]"
              }`}
            >
              {e.rank}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-[var(--aiv-text)]">{e.name}</p>
              <p className="font-mono-data text-xs text-[var(--aiv-text-2)]">{e.callsign}</p>
            </div>
            <span className="font-mono-data text-sm font-bold text-[var(--aiv-accent)]">
              {e.record}
            </span>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export const Leaderboard = () => {
  const [period, setPeriod] = useState("alltime");
  const [monthLabel, setMonthLabel] = useState("");
  const [data, setData] = useState({
    alltime: { flights: [], hours: [] },
    month: { flights: [], hours: [] },
  });
  // Tracks whether each period's fetch has settled (success or final failure) —
  // distinct from "data is empty," so the UI can tell "still loading" apart
  // from "genuinely no flights" instead of showing the empty-state copy for both.
  const [loadedPeriods, setLoadedPeriods] = useState({ alltime: false, month: false });

  useEffect(() => {
    getLeaderboardMonthLabel().then(setMonthLabel).catch(() => {});
  }, []);

  useEffect(() => {
    if (loadedPeriods[period]) return;
    let cancelled = false;
    const fetchBoth = () =>
      Promise.all([getLeaderboard("flights", period), getLeaderboard("hours", period)]);
    const markLoaded = () => {
      if (!cancelled) setLoadedPeriods((l) => ({ ...l, [period]: true }));
    };
    const applyResult = ([flights, hours]) => {
      if (cancelled) return;
      setData((d) => ({ ...d, [period]: { flights, hours } }));
      markLoaded();
    };
    fetchBoth()
      .then(applyResult)
      .catch(() => {
        // First attempt can hit a cold backend cache (slow upstream refresh) and
        // fail; retry once rather than leaving the section empty until the user
        // happens to trigger a re-render (e.g. by switching the period toggle).
        setTimeout(() => {
          if (cancelled) return;
          fetchBoth().then(applyResult).catch(markLoaded);
        }, 3000);
      });
    return () => {
      cancelled = true;
    };
  }, [period, loadedPeriods]);

  const current = data[period];
  const isLoading = !loadedPeriods[period];
  const periodBlurb =
    period === "month"
      ? `Top pilots this month${monthLabel ? ` — ${monthLabel}` : ""}. Refreshed every 15 minutes.`
      : "Our most dedicated aviators, ranked by all-time flights flown and hours in the cockpit.";

  return (
    <section id="leaderboard" data-testid="leaderboard-section" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-4xl px-5 lg:px-10">
        <div className="mb-12 text-center">
          <span className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full border border-[var(--aiv-accent)]/30 bg-[var(--aiv-accent)]/5">
            <Trophy className="h-6 w-6 text-[var(--aiv-accent)]" />
          </span>
          <p className="text-overline text-[#8A1538]">Hall of fame</p>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-[var(--aiv-text)] lg:text-4xl">
            Pilot Leaderboards
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-[var(--aiv-text-2)]">{periodBlurb}</p>
        </div>

        {/* Period toggle */}
        <div
          role="tablist"
          aria-label="Leaderboard period"
          data-testid="leaderboard-period-toggle"
          className="mx-auto mb-6 flex w-full max-w-xs items-center rounded-full border border-white/10 bg-[var(--aiv-surface)] p-1"
        >
          {[
            { key: "alltime", label: "All-Time" },
            { key: "month", label: monthLabel || "This Month", icon: Calendar },
          ].map((p) => {
            const active = period === p.key;
            const Icon = p.icon;
            return (
              <button
                key={p.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setPeriod(p.key)}
                data-testid={`leaderboard-period-${p.key}`}
                className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors duration-200 ${
                  active ? "bg-[var(--aiv-accent)] text-[#0A0B10]" : "text-[var(--aiv-text-2)] hover:text-[var(--aiv-text)]"
                }`}
              >
                {Icon && <Icon className="h-3.5 w-3.5" />} {p.label}
              </button>
            );
          })}
        </div>

        <Tabs defaultValue="flights" className="w-full">
          <TabsList
            data-testid="leaderboard-tabs"
            className="mx-auto mb-8 grid w-full max-w-xs grid-cols-2 rounded-full border border-white/10 bg-[var(--aiv-surface)] p-1"
          >
            <TabsTrigger
              value="flights"
              data-testid="leaderboard-tab-flights"
              className="rounded-full text-sm data-[state=active]:bg-[#8A1538] data-[state=active]:text-white"
            >
              <Plane className="mr-2 h-4 w-4" /> Flights
            </TabsTrigger>
            <TabsTrigger
              value="hours"
              data-testid="leaderboard-tab-hours"
              className="rounded-full text-sm data-[state=active]:bg-[#8A1538] data-[state=active]:text-white"
            >
              <Clock className="mr-2 h-4 w-4" /> Hours
            </TabsTrigger>
          </TabsList>

          <TabsContent value="flights">
            <Rows entries={current.flights} isLoading={isLoading} />
          </TabsContent>
          <TabsContent value="hours">
            <Rows entries={current.hours} isLoading={isLoading} />
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};
